import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminLayout from "../../../layouts/admin/AdminLayout";
import api from "../../../api/axios";

export default function AdminEmploymentList() {
  const nav = useNavigate();

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0); // 0-based
  const [size] = useState(10);

  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fmtDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}.${mm}.${dd}`;
  };

  const load = async (pageIndex = 0) => {
    setLoading(true);
    setError("");

    try {
      const res = await api.get("/api/employments/admin/list", {
        params: { page: pageIndex, size },
      });

      const resultCode = res.data?.resultCode;
      if (resultCode !== "200" && resultCode !== 200) {
        setError(
          res.data?.resultMessage || "채용공고 목록을 불러오지 못했습니다.",
        );
        setItems([]);
        return;
      }

      const data = res.data?.data;

      if (data && Array.isArray(data.content)) {
        // Spring Data Page
        setItems(data.content);
        setPage(data.number ?? pageIndex);
        setTotalPages(data.totalPages ?? 1);
        setTotalElements(data.totalElements ?? data.content.length);
      } else if (Array.isArray(data)) {
        // 그냥 배열
        setItems(data);
        setPage(0);
        setTotalPages(1);
        setTotalElements(data.length);
      } else {
        setItems([]);
        setTotalPages(1);
        setTotalElements(0);
      }
    } catch (e) {
      console.error(e);
      setError(
        e.response?.data?.resultMessage ||
          "채용공고 목록을 불러오는 중 오류가 발생했습니다.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const onPrev = () => setPage((p) => Math.max(0, p - 1));
  const onNext = () => setPage((p) => Math.min(totalPages - 1, p + 1));

  return (
    <AdminLayout>
      {/* 헤더 영역 */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-extrabold tracking-tight text-blue-900 md:text-3xl">
          채용공고 관리
        </h1>
        <button
          type="button"
          onClick={() => nav("/admin/employment/new")}
          className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
        >
          채용공고 등록
        </button>
      </div>

      <div className="rounded-2xl border bg-white p-4 shadow-sm md:p-6">
        {/* 상태영역 */}
        {loading && (
          <div className="py-10 text-center text-slate-500">
            채용공고를 불러오는 중입니다...
          </div>
        )}

        {!loading && error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="py-10 text-center text-slate-500">
            등록된 채용공고가 없습니다.
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            {/* 총 개수 */}
            <div className="mb-3 text-xs text-slate-500">
              총{" "}
              <span className="font-semibold text-slate-700">
                {totalElements}
              </span>{" "}
              건
            </div>

            {/* 테이블 */}
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b bg-slate-50 text-xs font-semibold text-slate-600">
                  <tr>
                    <th className="px-3 py-2">번호</th>
                    <th className="px-3 py-2">제목</th>
                    <th className="px-3 py-2">회사명</th>
                    <th className="px-3 py-2">마감일</th>
                    <th className="px-3 py-2">작성일</th>
                    <th className="px-3 py-2 text-center">보기</th>
                  </tr>
                </thead>

                <tbody>
                  {items.map((item, idx) => {
                    // 페이지 기준 역순 번호
                    const no = totalElements - (page * size + idx);

                    return (
                      <tr
                        key={item.id}
                        className="border-b last:border-b-0 hover:bg-slate-50"
                      >
                        <td className="px-3 py-2 align-middle text-xs text-slate-500">
                          {no > 0 ? no : ""}
                        </td>

                        <td className="px-3 py-2 align-middle">
                          <Link
                            to={`/admin/employment/${item.id}`}
                            className="text-sm font-medium text-slate-900 hover:text-blue-700"
                          >
                            {item.title}
                          </Link>
                        </td>

                        <td className="px-3 py-2 align-middle text-xs text-slate-700">
                          {item.company}
                        </td>

                        <td className="px-3 py-2 align-middle text-xs text-slate-500">
                          {fmtDate(item.deadline)}
                        </td>

                        <td className="px-3 py-2 align-middle text-xs text-slate-500">
                          {fmtDate(item.createdAt)}
                        </td>

                        <td className="px-3 py-2 align-middle text-center">
                          <Link
                            to={`/admin/employment/${item.id}`}
                            className="inline-flex items-center rounded-lg border px-3 py-1 text-xs text-slate-700 hover:bg-slate-50"
                          >
                            상세
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* 페이지네이션 */}
            <div className="mt-4 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={onPrev}
                disabled={page <= 0}
                className="rounded-lg border px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                이전
              </button>
              <span className="text-xs text-slate-500">
                {page + 1} / {totalPages}
              </span>
              <button
                type="button"
                onClick={onNext}
                disabled={page >= totalPages - 1}
                className="rounded-lg border px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                다음
              </button>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
